import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavItem {
  label: string;
  path?: string;
  children?: { label: string; path: string; icon?: string }[];
}

const navItems: NavItem[] = [
  { label: '關於兄弟維修', path: '/about' },
  { label: '最新消息', path: '/news' },
  {
    label: '維修項目',
    children: [
      { label: 'iPhone 維修', path: '/services/iphone', icon: 'ri-smartphone-line' },
      { label: 'iPad 維修', path: '/services/ipad', icon: 'ri-tablet-line' },
      { label: 'Mac 維修', path: '/services/mac', icon: 'ri-macbook-line' },
      { label: '手機資料救援', path: '/services/data-recovery', icon: 'ri-hard-drive-3-line' },
      { label: '常見問題', path: '/faq', icon: 'ri-question-line' },
    ],
  },
  { label: '維修價格', path: '/pricing' },
  {
    label: '案例分享',
    children: [
      { label: '維修案例', path: '/cases', icon: 'ri-tools-line' },
    ],
  },
];

function DropdownMenu({ items, visible }: { items: { label: string; path: string; icon?: string }[]; visible: boolean }) {
  if (!visible) return null;
  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-gray-100 rounded-2xl py-2 min-w-48 z-50 shadow-lg shadow-black/5">
      {items.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className="flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 text-gray-700 hover:text-orange-500 text-sm transition-colors cursor-pointer"
        >
          {item.icon && (
            <div className="w-5 h-5 flex items-center justify-center text-orange-400">
              <i className={item.icon} />
            </div>
          )}
          {item.label}
        </Link>
      ))}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  const handleMouseEnter = (label: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenDropdown(label);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  const isActiveParent = (item: NavItem) => {
    if (item.path) return location.pathname === item.path;
    return item.children?.some((c) => location.pathname.startsWith(c.path)) ?? false;
  };

  const textClass = scrolled || !isHome ? 'text-gray-700' : 'text-white';
  const activeTextClass = 'text-orange-500';

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled || !isHome ? 'bg-white border-b border-gray-100' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center cursor-pointer">
          <img
            src="https://static.readdy.ai/image/15419bedb578ec59343dd2ae27ec91d5/bcd8c2dce83612dc3328f52b56b1caf2.png"
            alt="兄弟維修"
            className="h-10 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => {
            const active = isActiveParent(item);
            if (item.children) {
              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      active
                        ? activeTextClass
                        : scrolled || !isHome
                        ? `${textClass} hover:text-orange-500 hover:bg-gray-50`
                        : 'text-white hover:text-orange-300'
                    }`}
                  >
                    {item.label}
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-arrow-down-s-line text-sm transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                  <DropdownMenu items={item.children} visible={openDropdown === item.label} />
                </div>
              );
            }
            return (
              <Link
                key={item.path}
                to={item.path!}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  active
                    ? `${activeTextClass} bg-orange-50`
                    : scrolled || !isHome
                    ? `${textClass} hover:text-orange-500 hover:bg-gray-50`
                    : 'text-white hover:text-orange-300'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="hidden lg:flex items-center">
          <button
            onClick={() => navigate('/pricing')}
            className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer"
          >
            立即查詢報價
          </button>
        </div>

        {/* Mobile Button */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer ${
            scrolled || !isHome ? 'text-gray-700' : 'text-white'
          }`}
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-4 py-3 flex flex-col gap-1">
          {navItems.map((item) => {
            if (item.children) {
              return (
                <div key={item.label}>
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    {item.label}
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-arrow-down-s-line transition-transform ${mobileExpanded === item.label ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                  {mobileExpanded === item.label && (
                    <div className="ml-4 pl-4 border-l-2 border-orange-100 mb-1">
                      {item.children.map((child) => (
                        <Link key={child.path} to={child.path} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:text-orange-500 cursor-pointer">
                          {child.icon && <i className={`${child.icon} text-orange-400`} />}
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link key={item.path} to={item.path!} className={`px-4 py-3 rounded-xl text-sm font-medium cursor-pointer ${location.pathname === item.path ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:bg-gray-50'}`}>
                {item.label}
              </Link>
            );
          })}
          <button onClick={() => navigate('/pricing')} className="mt-2 py-3 bg-orange-500 text-white text-sm font-semibold rounded-full cursor-pointer">
            立即查詢報價
          </button>
        </div>
      )}
    </header>
  );
}
